import type {NormalizedCacheObject} from '@apollo/client';
import {
    type ApolloClientProxy,
    type BaseHttpCodes,
    EntityBaseGqlService,
    type GetDownloadLinkRequestPayload,
    type IDownloadByLinkService,
    type ServiceResponsePayload,
} from '@igp/shared/api';
import type {
    Mutation,
    Query,
    QueryGetAgentNegativeCarryoverReportArgs,
    QueryGetWeeklySummaryExportArgs,
    Subscription,
    WeeklySummaryExport,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';
import type {AgentLevelReportV2ServerFilterKeys} from '@features/agent-level-report-v2';

import {NegativeCarryoverReportBuilder, NegativeCarryoverWeeklySummaryRequestBuilder} from './reportBuilder';
import {
    type AgentNegativeCarryoverReportExtended,
    generalItemId,
    type NegativeCarryoverReportDownloadFields,
    type NegativeCarryoverReportQueryFields,
    type NegativeCarryoverWeeklySummaryDownloadFields,
    type NegativeCarryoverWeeklySummaryQueryFields,
} from './types';

@injectable()
export class NegativeCarryoverReportService<TCodes extends BaseHttpCodes | string>
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetAgentNegativeCarryoverReportArgs,
        NegativeCarryoverReportQueryFields,
        Mutation,
        Subscription,
        TCodes
    >
    implements IDownloadByLinkService<NegativeCarryoverReportDownloadFields>
{
    protected readonly _reportRequestBuilder: NegativeCarryoverReportBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer<TCodes>
    ) {
        const builder = new NegativeCarryoverReportBuilder();
        super(client, builder, messageLocalizer);
        this._reportRequestBuilder = builder;
    }

    protected getErrorSeverity(_code: TCodes): number {
        return 0;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, NegativeCarryoverReportQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, AgentNegativeCarryoverReportExtended>> {
        return this.fetchData(requestPayload).pipe(
            map(({status, message, errors, responsePayload}) => {
                const last_updated = responsePayload?.getAgentNegativeCarryoverReport?.last_updated;

                const generalItem: AgentNegativeCarryoverReportExtended = {
                    id: generalItemId,
                    agent_uid: undefined,
                    is_general: true,
                    last_updated,
                    week_start_date_ts: undefined,
                };
                const items = responsePayload?.getAgentNegativeCarryoverReport?.items ?? [];
                const total = responsePayload?.getAgentNegativeCarryoverReport?.total_count
                    ? responsePayload?.getAgentNegativeCarryoverReport?.total_count
                    : items.length;
                return {
                    status,
                    message,
                    errors,
                    requestPayload,
                    responsePayload: {
                        items: [generalItem, ...items],
                        total,
                    },
                };
            })
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<NegativeCarryoverReportDownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<NegativeCarryoverReportDownloadFields>, string>> {
        const request = this._reportRequestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportAgentNegativeCarryoverReport?.url,
            }))
        );
    }
}

@injectable()
export class NegativeCarryoverWeeklySummaryService
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetWeeklySummaryExportArgs,
        NegativeCarryoverWeeklySummaryQueryFields,
        Mutation,
        Subscription,
        BaseHttpCodes,
        AgentLevelReportV2ServerFilterKeys
    >
    implements IDownloadByLinkService<NegativeCarryoverWeeklySummaryDownloadFields>
{
    protected readonly _reportRequestBuilder: NegativeCarryoverWeeklySummaryRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const builder = new NegativeCarryoverWeeklySummaryRequestBuilder();
        super(client, builder, messageLocalizer);
        this._reportRequestBuilder = builder;
    }

    protected getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, NegativeCarryoverWeeklySummaryQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, WeeklySummaryExport>> {
        return this.fetchData(requestPayload).pipe(
            map(({status, message, errors, responsePayload}) => {
                return {
                    status,
                    message,
                    errors,
                    requestPayload,
                    responsePayload: {
                        items: responsePayload?.getWeeklySummaryExport?.items ?? [],
                        total: responsePayload?.getWeeklySummaryExport?.total_count ?? 0,
                    },
                };
            })
        );
    }

    getDownloadLink(
        requestPayload: GetDownloadLinkRequestPayload<NegativeCarryoverWeeklySummaryDownloadFields>
    ): Observable<ServiceResponsePayload<GetDownloadLinkRequestPayload<NegativeCarryoverWeeklySummaryDownloadFields>, string>> {
        const request = this._reportRequestBuilder.buildDownloadRequest(requestPayload);
        return this._service.query(request.query, request.variables).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: response?.responsePayload?.exportWeeklySummary?.url,
            }))
        );
    }
}
